import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Coins } from "lucide-react";
import { NAV_LINKS, LANGUAGES } from "@/data/content";
import { GoldButton } from "@/components/GoldButton";
import { useWaitlist } from "@/context/WaitlistContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Logo = () => (
  <a href="#hero" data-testid="nav-logo" className="flex items-center gap-2.5">
    <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-gold via-gold-amber to-gold-rose shadow-[0_4px_14px_-2px_rgba(212,175,55,0.5)]">
      <Coins className="h-5 w-5 text-charcoal-deep" strokeWidth={2.2} />
    </span>
    <span className="font-serif text-2xl tracking-tight text-charcoal">Shreedhan</span>
  </a>
);

export const Navbar = () => {
  const { openWaitlist } = useWaitlist();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState("EN");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <nav
        className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-500 sm:px-8 ${
          scrolled
            ? "my-3 rounded-full border border-amber-200/50 bg-white/75 py-2.5 shadow-[0_8px_32px_rgba(42,36,32,0.06)] backdrop-blur-xl md:mx-auto md:max-w-6xl"
            : "py-5"
        }`}
      >
        <Logo />

        <div className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.replace("#", "")}`}
              className="group relative text-sm font-medium text-charcoal-soft transition-colors hover:text-charcoal"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gradient-to-r from-gold to-gold-amber transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-toggle"
                className="hidden items-center gap-1 rounded-full border border-charcoal/10 bg-white/50 px-3 py-2 text-sm font-medium text-charcoal transition-colors hover:border-gold/40 sm:flex"
              >
                {lang} <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px] rounded-xl border-amber-200/60 bg-ivory-light">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l}
                  data-testid={`lang-option-${l}`}
                  onClick={() => setLang(l)}
                  className="cursor-pointer text-charcoal focus:bg-gold/10 focus:text-charcoal"
                >
                  {l === "EN" ? "English" : l === "हिं" ? "हिंदी" : l === "ਪੰ" ? "ਪੰਜਾਬੀ" : "தமிழ்"}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <GoldButton
            data-testid="nav-start-saving-btn"
            size="sm"
            className="hidden sm:inline-flex"
            onClick={() => window.open("https://shreedhani.com/", "_blank", "noopener,noreferrer")}
          >
            Start Saving
          </GoldButton>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal/10 bg-white/60 text-charcoal lg:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            data-testid="mobile-menu"
            className="mx-4 mt-2 rounded-3xl border border-amber-200/50 bg-white/90 p-6 shadow-xl backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-medium text-charcoal-soft hover:text-charcoal"
                >
                  {link.label}
                </a>
              ))}
              <GoldButton
                data-testid="mobile-start-saving-btn"
                className="mt-2 w-full"
                onClick={() => { setMobileOpen(false); window.open("https://shreedhani.com/", "_blank", "noopener,noreferrer"); }}
              >
                Start Saving
              </GoldButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
